/*
  kaching-subscriptions-ui.js

  Enhances the Kaching Subscriptions widget to match the Full Cart PDP design,
  sitting NATIVELY ON TOP of the app — without injecting interactive nodes into
  Kaching's reactive subtree.

  Pieces:
  - Dynamic "voorkeur" badge next to the block title (all products).
  - Per-row quantity steppers in our OWN overlay layer (combi products), each row
    keeping its own quantity.
  - Per-option pricing: each option shows its OWN quantity × unit price. Kaching can
    only apply one global quantity to every option, so we own the price text instead:
    we keep the real name="quantity" at 1 (Kaching renders unit prices, never
    re-multiplies → no fighting), compute each row's qty × unit from the widget's own
    config, and write it into that row's price spans. The real quantity is set only at
    submit (the cart adds the selected option × its quantity).

  Scoped to .product-form. Loaded once on the product template.
*/
(function () {
  'use strict';

  var EDGE = 16; // stepper inset from the row's right edge

  function isCombi() {
    return !!document.querySelector('.pdp-pref:not(.pdp-pref--regular)');
  }

  function findRealQuantityInput() {
    return document.querySelector(
      '.product-form__container input[name="quantity"], .product-form input[name="quantity"], input[name="quantity"]'
    );
  }

  function parseAttr(blockEl, name) {
    if (!blockEl) return {};
    try {
      return JSON.parse(blockEl.getAttribute(name) || '{}');
    } catch (e) {
      return {};
    }
  }

  function buildPlanNames(product) {
    var map = {};
    var lists = [];
    (product.variants || []).forEach(function (v) {
      if (v && v.sellingPlans) lists.push(v.sellingPlans);
    });
    (product.sellingPlanGroups || []).forEach(function (g) {
      if (g && g.sellingPlans) lists.push(g.sellingPlans);
    });
    lists.forEach(function (plans) {
      plans.forEach(function (sp) {
        if (sp && sp.id != null && sp.name) map[String(sp.id)] = sp.name;
      });
    });
    return map;
  }

  // Unit prices (cents) per option, from the widget's own product config.
  function buildPriceInfo(product) {
    var info = { oneTime: null, plans: [] };
    var v =
      (product.variants || []).filter(function (x) {
        return x.id === product.selectedVariantId;
      })[0] || (product.variants || [])[0];
    if (!v) return info;
    info.oneTime = { sale: v.price, compare: v.compareAtPrice };
    (v.sellingPlans || []).forEach(function (sp) {
      info.plans.push({ name: sp.name, sale: sp.price, compare: v.compareAtPrice });
    });
    return info;
  }

  /* Resolve a widget row's plan data by the row's TITLE, never by array index.
     Since Juo Subscriptions was installed, the variant's sellingPlans list holds
     BOTH apps' plans (4 entries) while Kaching still renders 2 rows — positional
     mapping slid row 2 ("Elke twee maanden") onto Juo's "Elke maand" entry and
     painted a one-month price (€11,56/20%) on the two-month row (real charge:
     €13,01/10%). Both apps use identical plan names AND identical prices, so a
     name match is unambiguous either way. No match -> null: updatePrices() then
     leaves that row alone and Kaching's own (correct) price stays visible. */
  function planPriceByTitle(plans, title) {
    if (!title) return null;
    var t = title.trim();
    for (var i = 0; i < plans.length; i++) {
      if (plans[i].name && plans[i].name.trim() === t) return plans[i];
    }
    return null;
  }

  // Formatter driven by the widget's moneyFormat (e.g. "€{{amount_with_comma_separator}}").
  function makeFormatter(moneyFormat) {
    var tmpl = moneyFormat || '€{{amount_with_comma_separator}}';
    var parts = tmpl.split(/\{\{[^}]*\}\}/);
    var prefix = parts[0] || '';
    var suffix = parts.length > 1 ? parts[1] : '';
    return function (cents) {
      if (cents == null || isNaN(cents)) return '';
      var s = (Math.round(cents) / 100).toFixed(2).split('.');
      var intPart = s[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return prefix + intPart + ',' + s[1] + suffix;
    };
  }

  function setup(widget) {
    var blockEl = document.querySelector('kaching-subscriptions-block');
    var product = parseAttr(blockEl, 'product');
    var config = parseAttr(blockEl, 'config');
    var planNames = buildPlanNames(product);
    var priceInfo = buildPriceInfo(product);
    var fmt = makeFormatter(config.moneyFormat);

    var combi = isCombi();
    // Regular (non-bundle) pages get the same per-row steppers and per-row
    // prices as the bundle pages: hide the standalone price+quantity row (its
    // hidden name="quantity" input keeps submitting) and own the rows instead.
    // Products without the Kaching widget never reach setup(), so they keep
    // the standalone row untouched.
    var regularBlock = combi ? null : document.querySelector('.pdp-pref--regular');
    if (regularBlock) regularBlock.classList.add('pdp-pref--ks-owned');
    var realInput = combi
      ? findRealQuantityInput()
      : (regularBlock && regularBlock.querySelector('[data-pdp-regular-quantity]')) || findRealQuantityInput();

    var minQty = 1;
    var maxQty = Infinity;
    if (realInput) {
      var pMin = parseInt(realInput.getAttribute('min'), 10);
      var pMax = parseInt(realInput.getAttribute('max'), 10);
      if (!isNaN(pMin) && pMin > 0) minQty = pMin;
      if (!isNaN(pMax) && pMax > 0) maxQty = pMax;
    }
    function clamp(n) {
      if (isNaN(n)) n = minQty;
      if (n < minQty) n = minQty;
      if (n > maxQty) n = maxQty;
      return n;
    }

    var qtyByKey = {};
    var stepperByKey = {};
    var discountBadgeByKey = {};
    var positionCtx =
      widget.closest('product-form') || widget.closest('.product-form__container') || widget.parentElement;
    var overlay = null;

    /* ---------------- Badge ---------------- */
    function ensureBadge() {
      var wrap = widget.querySelector('.kaching-subscriptions__block-title-wrapper');
      if (!wrap) return null;
      var badge = wrap.querySelector('.kaching-subscriptions__voorkeur-badge');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'kaching-subscriptions__voorkeur-badge';
        wrap.appendChild(badge);
      }
      return badge;
    }
    function selectedLabel() {
      if (widget.querySelector('.kaching-subscriptions__card-wrapper.one-time-purchase.selected')) {
        return 'Eenmalig';
      }
      var planId = widget.getAttribute('data-selling-plan-id');
      if (planId && planNames[planId]) return planNames[planId];
      var radio = widget.querySelector(
        '.kaching-subscriptions__selling-plan .kaching-subscriptions__card-radio.selected'
      );
      if (radio) {
        var row = radio.closest('.kaching-subscriptions__selling-plan');
        var title = row && row.querySelector('.kaching-subscriptions__selling-plan-title');
        if (title) return title.textContent.trim();
      }
      return 'Eenmalig';
    }
    function updateBadge() {
      var badge = ensureBadge();
      if (!badge) return;
      var text = selectedLabel();
      if (badge.textContent !== text) badge.textContent = text;
    }

    /* ---------------- Rows ---------------- */
    function getRows() {
      var list = [];
      var oneTime = widget.querySelector('.kaching-subscriptions__card-wrapper.one-time-purchase');
      if (oneTime) {
        var card = oneTime.querySelector('.kaching-subscriptions__card');
        if (card) {
          list.push({
            key: 'one-time',
            rowEl: card,
            price: priceInfo.oneTime,
            selected: function () { return oneTime.classList.contains('selected'); },
          });
        }
      }
      var plans = widget.querySelectorAll('.kaching-subscriptions__selling-plan');
      Array.prototype.forEach.call(plans, function (plan, i) {
        var titleEl = plan.querySelector('.kaching-subscriptions__selling-plan-title');
        list.push({
          key: 'plan-' + i,
          rowEl: plan,
          price: planPriceByTitle(priceInfo.plans, titleEl && titleEl.textContent),
          selected: function () {
            return !!plan.querySelector('.kaching-subscriptions__card-radio.selected');
          },
        });
      });
      return list;
    }

    /* ---------------- Bundle context ----------------
       The package (Alleen/Bundel) + kit (Trial/Combi) selection lives in the theme's
       pdp-pref, outside the widget. Under Bundel, each option price adds the chosen
       2nd product (one-time, qty 1), matching how product-form.js#addBundleToCart adds
       to the cart (main × qty + 2nd × 1). */
    function getBundleSecondPrice() {
      var pref = document.querySelector('.pdp-pref:not(.pdp-pref--regular)');
      if (!pref) return 0;
      var pkg = pref.querySelector('[data-option].active');
      if (!pkg || pkg.getAttribute('data-option') !== 'bundle') return 0;
      var kit = pref.querySelector('[data-bundle-option].active');
      var type = kit && kit.getAttribute('data-bundle-option');
      if (type === 'combi-kit' && kit) {
        return parseInt(kit.getAttribute('data-combi-kit-price'), 10) || 0;
      }
      if (type === 'trial-kit') {
        // Trial Kit includes a 2nd product only once one has actually been ADDED
        // (mirrors [data-added-bundle-image] visibility / pdp-pref.js + the cart's
        // getSecondVariantIdForBundle). Empty Trial Kit = main product only.
        var added = document.querySelector('[data-added-bundle-image]');
        if (!added || added.classList.contains('hidden')) return 0;
        var c = document.querySelector('[data-combineer-variant-price]:checked');
        if (c) return parseInt(c.getAttribute('data-combineer-variant-price'), 10) || 0;
      }
      return 0;
    }

    /* The active kit's REAL bundle discount % (checkout-enforced by the bundle
       discount function app). Trial Kit only discounts once a 2nd product is
       added — no pair in the cart means the function applies nothing. */
    function getBundleDiscountPercent() {
      var pref = document.querySelector('.pdp-pref:not(.pdp-pref--regular)');
      if (!pref) return 0;
      var pkg = pref.querySelector('[data-option].active');
      if (pkg && pkg.getAttribute('data-option') !== 'bundle') return 0;
      var kit = pref.querySelector('[data-bundle-option].active');
      var type = kit && kit.getAttribute('data-bundle-option');
      if (type === 'combi-kit') {
        return parseFloat(pref.getAttribute('data-combi-discount-percent')) || 0;
      }
      if (type === 'trial-kit') {
        var added = document.querySelector('[data-added-bundle-image]');
        if (!added || added.classList.contains('hidden')) return 0;
        return parseFloat(pref.getAttribute('data-trial-discount-percent')) || 0;
      }
      return 0;
    }

    /* Kaching's struck "was" price is a plain span (.kaching-subscriptions__
       original-price, confirmed via on-page diagnostic) struck through with
       CSS — not an <s>/<del> and no "compare" class. Fast-path its class;
       fall back to a computed-style scan so a Kaching rename can't silently
       break the anchor again. line-through paints onto descendants without
       appearing in their computed style, so on a style match descend to the
       text leaf before writing. */
    function findCompareEl(rowEl) {
      var scopes = [rowEl];
      if (rowEl.parentElement) scopes.push(rowEl.parentElement);
      for (var i = 0; i < scopes.length; i++) {
        var el =
          scopes[i].querySelector('[class*="original-price"], [class*="compare"]') ||
          scopes[i].querySelector('s, del');
        if (el) return el;
      }
      for (var j = 0; j < scopes.length; j++) {
        var nodes = scopes[j].querySelectorAll('*');
        for (var k = 0; k < nodes.length; k++) {
          var n = nodes[k];
          var deco = window.getComputedStyle(n).textDecorationLine || '';
          if (deco.indexOf('line-through') === -1) continue;
          if (!/\d/.test(n.textContent || '')) continue;
          while (n.childElementCount) {
            var next = null;
            for (var c = 0; c < n.children.length; c++) {
              if (/\d/.test(n.children[c].textContent || '')) {
                next = n.children[c];
                break;
              }
            }
            if (!next) break;
            n = next;
          }
          return n;
        }
      }
      return null;
    }

    /* ---------------- Per-option pricing ----------------
       We own the price text so each row reflects its OWN quantity (and the bundle
       2nd product when Bundel is active). Kaching is kept at qty 1 so it never
       re-multiplies and never fights us. The bundle discount % applies to BOTH
       lines at checkout (stacking on top of the subscription price is the
       agreed behaviour), so the shown total is (main×qty + 2nd) × (1 − pct). */
    function updatePrices() {
      var second = getBundleSecondPrice();
      var factor = 1 - getBundleDiscountPercent() / 100;
      getRows().forEach(function (row) {
        if (!row.price) return;
        var qty = qtyByKey[row.key] || minQty;
        var saleEl = row.rowEl.querySelector(
          '.kaching-subscriptions__card-price, .kaching-subscriptions__selling-plan-price'
        );
        if (saleEl && row.price.sale != null) {
          var t = fmt(Math.round((row.price.sale * qty + second) * factor));
          if (t && saleEl.textContent !== t) saleEl.textContent = t;
        }
        // Kaching renders the struck price from its own data (main product's
        // compare-at only) — under Bundel that reads lower than the payable pair
        // price. With a bundle discount the anchor is the row's UNDISCOUNTED
        // pair total; without one, the original compare-at behaviour stands.
        // The struck node's shape varies (class containing "compare", or a
        // plain <s>/<del>), and it can sit beside — not inside — the one-time
        // row's card element, so probe row then parent, class then tag.
        var cmpEl = findCompareEl(row.rowEl);
        if (cmpEl && row.price.sale != null) {
          var full = null;
          if (factor < 1) {
            var oneTimeSale = priceInfo.oneTime && priceInfo.oneTime.sale;
            // One-time row anchors at the pair BEFORE the bundle korting; the
            // subscription rows anchor at the bundle's one-time price — each
            // strikethrough shows exactly what that row's choice saves.
            full =
              row.key === 'one-time' || oneTimeSale == null
                ? row.price.sale * qty + second
                : (oneTimeSale * qty + second) * factor;
          } else if (row.price.compare != null && row.price.compare > row.price.sale) {
            full = row.price.compare * qty + second;
          }
          if (full != null) {
            var tc = fmt(Math.round(full));
            if (tc && cmpEl.textContent !== tc) cmpEl.textContent = tc;
          }
        }
      });
    }

    /* ---------------- Overlay steppers ---------------- */
    // The overlay hosts the discount pills on EVERY product page; only the
    // steppers and price-owning inside it are bundle-page (combi) features.
    function ensureOverlay() {
      if (!positionCtx) return null;
      if (overlay && overlay.isConnected) return overlay;
      overlay = positionCtx.querySelector(':scope > .kaching-subscriptions__qty-overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'kaching-subscriptions__qty-overlay';
        positionCtx.appendChild(overlay);
      }
      return overlay;
    }

    function buildStepper(key) {
      var wrap = document.createElement('div');
      wrap.className = 'kaching-subscriptions__qty quantity';
      wrap.setAttribute('data-ks-qty', key);

      var minus = document.createElement('button');
      minus.type = 'button';
      minus.className = 'quantity__button';
      minus.setAttribute('aria-label', 'Verlaag aantal');
      minus.textContent = '−';

      var val = document.createElement('span');
      val.className = 'quantity__input';
      val.setAttribute('role', 'status');
      val.setAttribute('aria-live', 'polite');
      val.textContent = String(qtyByKey[key]);

      var plus = document.createElement('button');
      plus.type = 'button';
      plus.className = 'quantity__button';
      plus.setAttribute('aria-label', 'Verhoog aantal');
      plus.textContent = '+';

      function step(delta) {
        qtyByKey[key] = clamp((qtyByKey[key] || minQty) + delta);
        val.textContent = String(qtyByKey[key]);
        updatePrices();
      }
      minus.addEventListener('click', function () { step(-1); });
      plus.addEventListener('click', function () { step(1); });

      wrap.appendChild(minus);
      wrap.appendChild(val);
      wrap.appendChild(plus);
      return wrap;
    }

    function ensureStepper(key) {
      if (stepperByKey[key] && stepperByKey[key].isConnected) return stepperByKey[key];
      if (qtyByKey[key] == null) qtyByKey[key] = minQty;
      var ov = ensureOverlay();
      if (!ov) return null;
      var s = buildStepper(key);
      ov.appendChild(s);
      stepperByKey[key] = s;
      return s;
    }

    function positionSteppers() {
      var ov = ensureOverlay();
      if (!ov) return;
      var crect = positionCtx.getBoundingClientRect();
      var present = {};
      getRows().forEach(function (row) {
        present[row.key] = true;
        var s = ensureStepper(row.key);
        if (!s) return;
        s.style.display = '';
        var valEl = s.querySelector('.quantity__input');
        if (valEl && valEl.textContent !== String(qtyByKey[row.key])) {
          valEl.textContent = String(qtyByKey[row.key]);
        }
        var rrect = row.rowEl.getBoundingClientRect();
        var left = rrect.right - crect.left - s.offsetWidth - EDGE;
        var top = rrect.top - crect.top + (rrect.height - s.offsetHeight) / 2;
        s.style.transform = 'translate(' + Math.round(left) + 'px,' + Math.round(top) + 'px)';
      });
      Object.keys(stepperByKey).forEach(function (k) {
        if (!present[k]) stepperByKey[k].style.display = 'none';
      });
    }

    /* ---------------- Per-option discount badge ("X% Korting") ----------------
       Small pill next to each subscription plan's caption, in OUR overlay layer
       (same approach as the steppers — never written into Kaching's reactive DOM).
       Value = the plan's subscription discount vs the regular one-time price, which
       matches the app's configured "Save X%". One-time gets none. */
    function planDiscountPercent(planSale) {
      var oneTime = priceInfo.oneTime && priceInfo.oneTime.sale;
      if (!oneTime || planSale == null || planSale >= oneTime) return 0;
      return Math.round(((oneTime - planSale) / oneTime) * 100);
    }

    function ensureDiscountBadge(key) {
      if (discountBadgeByKey[key] && discountBadgeByKey[key].isConnected) return discountBadgeByKey[key];
      var ov = ensureOverlay();
      if (!ov) return null;
      var b = document.createElement('span');
      b.className = 'kaching-subscriptions__discount-badge';
      b.setAttribute('data-ks-discount', key);
      ov.appendChild(b);
      discountBadgeByKey[key] = b;
      return b;
    }

    function positionDiscountBadges() {
      var ov = ensureOverlay();
      if (!ov) return;
      var crect = positionCtx.getBoundingClientRect();
      var present = {};
      getRows().forEach(function (row) {
        if (row.key === 'one-time' || !row.price || row.price.sale == null) return;
        var b = ensureDiscountBadge(row.key);
        if (!b) return;
        var pct = planDiscountPercent(row.price.sale);
        if (pct <= 0) {
          b.style.display = 'none';
          return;
        }
        present[row.key] = true;
        b.style.display = '';
        var text = pct + '% Korting';
        if (b.textContent !== text) b.textContent = text;
        var anchor = row.rowEl.querySelector('.kaching-subscriptions__selling-plan-title') || row.rowEl;
        var arect = anchor.getBoundingClientRect();
        var left, top;
        if (window.matchMedia('(max-width: 768px)').matches) {
          // Mobile rows have no room between the caption and the stacked
          // prices — the override CSS reserves a lane above the caption
          // (row padding-top) and the pill sits in it, left-aligned.
          var rrect = row.rowEl.getBoundingClientRect();
          left = arect.left - crect.left;
          top = rrect.top - crect.top + 12;
        } else {
          // Desktop: just after the caption ("Elke maand"), centered on it,
          // clamped so it can never paint over the price column.
          left = arect.right - crect.left + 8;
          top = arect.top - crect.top + (arect.height - b.offsetHeight) / 2;
          var priceBox = row.rowEl.querySelector(
            '.kaching-subscriptions__card-price-wrapper, .kaching-subscriptions__selling-plan-price, .kaching-subscriptions__card-price'
          );
          if (priceBox) {
            var prect = priceBox.getBoundingClientRect();
            var maxLeft = prect.left - crect.left - b.offsetWidth - 8;
            if (left > maxLeft) left = Math.max(maxLeft, arect.left - crect.left);
          }
        }
        b.style.transform = 'translate(' + Math.round(left) + 'px,' + Math.round(top) + 'px)';
      });
      Object.keys(discountBadgeByKey).forEach(function (k) {
        if (!present[k]) discountBadgeByKey[k].style.display = 'none';
      });
    }

    /* ---------------- Cart quantity (set only at submit) ---------------- */
    function selectedQty() {
      var rows = getRows();
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].selected()) return clamp(qtyByKey[rows[i].key] != null ? qtyByKey[rows[i].key] : minQty);
      }
      return minQty;
    }
    if (realInput) {
      var form = realInput.form || widget.closest('form');
      if (form) {
        form.addEventListener(
          'submit',
          function () {
            // Set the real quantity for the cart, then restore to 1 so Kaching keeps
            // showing unit prices (the add builds its FormData synchronously here).
            realInput.value = String(selectedQty());
            setTimeout(function () { realInput.value = String(minQty); }, 0);
          },
          true
        );
      }
    }

    /* ---------------- Triggers ---------------- */
    var scheduled = false;
    function refresh() {
      scheduled = false;
      updateBadge();
      positionSteppers();
      positionDiscountBadges();
      updatePrices();
    }
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(refresh);
    }

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(schedule);
      ro.observe(widget);
      var cards = widget.querySelector('.kaching-subscriptions__cards-wrapper');
      if (cards) ro.observe(cards);
    }
    var attrObserver = new MutationObserver(schedule);
    attrObserver.observe(widget, { attributes: true, attributeFilter: ['data-selling-plan-id'] });
    widget.addEventListener('click', schedule);

    /* Kaching only toggles selection from its radio/label area — clicks on the
       rest of a row (price, empty space) do nothing. Make the WHOLE row a hit
       target by forwarding stray row clicks to that row's own radio. This is
       an event, not a DOM mutation, so the app's reactive subtree stays safe. */
    widget.addEventListener('click', function (e) {
      if (e.target.closest('input, button, a, label, .kaching-subscriptions__card-radio')) return;
      var row =
        e.target.closest('.kaching-subscriptions__selling-plan') ||
        e.target.closest('.kaching-subscriptions__card-wrapper');
      if (!row) return;
      var radio = row.querySelector('.kaching-subscriptions__card-radio, input[type="radio"]');
      if (radio) radio.click();
    });
    window.addEventListener('resize', schedule);

    // React to package/kit changes in the theme's pdp-pref (Alleen ↔ Bundel, Trial ↔ Combi)
    // and to trial-product changes in the bundle pop — all outside the widget.
    var pref = document.querySelector('.pdp-pref:not(.pdp-pref--regular)');
    if (pref) pref.addEventListener('click', schedule);
    document.querySelectorAll('[data-combineer-variant-price]').forEach(function (inp) {
      inp.addEventListener('change', schedule);
    });
    document
      .querySelectorAll('[data-add-bundle-submit-button], [data-remove-bundle-button]')
      .forEach(function (btn) {
        btn.addEventListener('click', function () { setTimeout(schedule, 0); });
      });

    refresh();
    requestAnimationFrame(refresh);
    setTimeout(refresh, 300);
  }

  function boot() {
    var widget = document.querySelector('.product-form .kaching-subscriptions__widget');
    if (widget) {
      setup(widget);
      return;
    }
    var bootObserver = new MutationObserver(function () {
      var w = document.querySelector('.product-form .kaching-subscriptions__widget');
      if (w) {
        bootObserver.disconnect();
        setup(w);
      }
    });
    bootObserver.observe(document.body, { subtree: true, childList: true });
    setTimeout(function () {
      bootObserver.disconnect();
    }, 15000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
